/**
 * Nutrient math — the single source of truth for building, summing, and scaling
 * Nutrients objects on the web app. Port of the mobile utils/nutrients.ts; keep
 * the two in sync so an entry logged on web is byte-identical to one from phone.
 *
 * Every construction/scale site MUST go through these helpers instead of
 * hand-enumerating fields. Hand-written field lists silently drop optional
 * nutrients (fiberG, sugarsG, transFatG, cholesterolMg, …) whenever
 * one is missed, which is how daily summaries end up reading 0 for a nutrient the
 * per-food breakdown clearly has. Iterating the canonical key list makes that
 * class of bug impossible.
 */

import type { Nutrients } from '@/types'

/** Core nutrients — always present on a logged food (mobile utils/nutrients.ts). */
export const CORE_NUTRIENT_KEYS = [
  'calories',
  'addedSugarsG',
  'saturatedFatG',
  'fiberG',
  'sodiumMg',
  'proteinG',
] as const

/** Optional nutrients — present only when tracked/available. */
export const OPTIONAL_NUTRIENT_KEYS = [
  'carbsG',
  'sugarsG',
  'fatG',
  'transFatG',
  'cholesterolMg',
] as const

/** Every nutrient key in Nutrients, core first. */
export const NUTRIENT_KEYS = [...CORE_NUTRIENT_KEYS, ...OPTIONAL_NUTRIENT_KEYS] as const

export type NutrientKey = (typeof NUTRIENT_KEYS)[number]

/** Keys rounded to one decimal place (grams / mcg); everything else to a whole number. */
const DECIMAL_KEYS = new Set<NutrientKey>([
  'addedSugarsG',
  'sugarsG',
  'carbsG',
  'fiberG',
  'fatG',
  'saturatedFatG',
  'transFatG',
  'proteinG',
])

/** Round a single nutrient value with the app's per-key convention. */
export function roundNutrient(key: NutrientKey, value: number): number {
  return DECIMAL_KEYS.has(key) ? Math.round(value * 10) / 10 : Math.round(value)
}

type NutrientLike = Partial<Nutrients> | null | undefined

/**
 * Sum a list of nutrient objects. An optional key is emitted only when at least
 * one input defines it (never `undefined` — Firestore rejects that). Core keys
 * are always emitted (default 0). Values are summed raw; scale with
 * {@link scaleNutrients} when you need rounded, storable output.
 */
export function sumNutrients(list: NutrientLike[]): Nutrients {
  const acc: Record<string, number> = {}
  const seen = new Set<NutrientKey>(CORE_NUTRIENT_KEYS)
  for (const key of CORE_NUTRIENT_KEYS) acc[key] = 0

  for (const n of list) {
    if (!n) continue
    for (const key of NUTRIENT_KEYS) {
      const v = (n as Record<string, number | undefined>)[key]
      if (v != null) {
        acc[key] = (acc[key] || 0) + v
        seen.add(key)
      }
    }
  }

  const out: Record<string, number> = {}
  for (const key of NUTRIENT_KEYS) {
    if (seen.has(key)) out[key] = acc[key] || 0
  }
  return out as unknown as Nutrients
}

/**
 * Scale a nutrient object by `factor`, rounding each key per convention. Only
 * keys present (non-null) on the input are emitted; core keys are always emitted.
 * Use for servings multiplication and per-serving division (factor = 1/servings).
 */
export function scaleNutrients(n: NutrientLike, factor: number): Nutrients {
  const src = (n || {}) as Record<string, number | undefined>
  const out: Record<string, number> = {}
  for (const key of CORE_NUTRIENT_KEYS) {
    out[key] = roundNutrient(key, (src[key] || 0) * factor)
  }
  for (const key of OPTIONAL_NUTRIENT_KEYS) {
    const v = src[key]
    if (v != null) out[key] = roundNutrient(key, v * factor)
  }
  return out as unknown as Nutrients
}

/**
 * Sum the nutrients of a list of food items (anything with a `.nutrients`).
 * Absent optional nutrients are omitted, present ones summed.
 */
export function totalsFromFoods(
  foods: Array<{ nutrients?: NutrientLike } | null | undefined>,
): Nutrients {
  return sumNutrients(foods.map((f) => f?.nutrients))
}

/**
 * Merge two nutrient objects, preferring `primary`. Core + any key present on
 * `primary` win; keys only present on `fallback` are backfilled. Used when a
 * server meal-total omits an optional nutrient that the per-food breakdown
 * carries, so top-level `nutrients` (and daily summaries) aren't zeroed.
 */
export function mergeNutrients(primary: NutrientLike, fallback: NutrientLike): Nutrients {
  const p = (primary || {}) as Record<string, number | undefined>
  const f = (fallback || {}) as Record<string, number | undefined>
  const out: Record<string, number> = {}
  for (const key of CORE_NUTRIENT_KEYS) {
    out[key] = p[key] ?? f[key] ?? 0
  }
  for (const key of OPTIONAL_NUTRIENT_KEYS) {
    const v = p[key] != null ? p[key] : f[key]
    if (v != null) out[key] = v
  }
  return out as unknown as Nutrients
}
